import { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import API_BASE_URL from '../config/api';

// Chat about one analysed detaljplan, with clickable [s. N] page citations.
// Trimmed adaptation of WikiChat (itself ported from the labs_system bid
// system): no attachments, no saved conversations, no document proposals.
// The conversation history lives in this component and is sent with each
// request - the backend is stateless.
//
// A bare [s. 14] is not markdown link syntax, so it survives parsing as a
// literal text node; this rehype plugin wraps the markers in <cite>, which we
// render as a button that opens the document viewer at that page.
const CITE_MARKER = /\[\s*s\.?\s*\d+(?:\s*[,–-]\s*\d+)*\s*\]/gi;

function rehypeCitations() {
  const walk = (node) => {
    if (!Array.isArray(node.children)) return;
    const out = [];
    for (const child of node.children) {
      if (child.type === 'text' && CITE_MARKER.test(child.value)) {
        CITE_MARKER.lastIndex = 0;
        const val = child.value;
        let last = 0;
        let m;
        while ((m = CITE_MARKER.exec(val)) !== null) {
          if (m.index > last) out.push({ type: 'text', value: val.slice(last, m.index) });
          out.push({
            type: 'element', tagName: 'cite', properties: {},
            children: [{ type: 'text', value: m[0] }],
          });
          last = m.index + m[0].length;
        }
        if (last < val.length) out.push({ type: 'text', value: val.slice(last) });
      } else {
        walk(child);
        out.push(child);
      }
    }
    node.children = out;
  };
  return (tree) => walk(tree);
}

const SUGGESTIONS = [
  'Vilka bindande planbestämmelser finns om buller?',
  'Hur hanteras skyfall och dagvatten?',
  'Vad innebär planen för barn och unga?',
];

const DPChat = ({ analysisId, onOpenPage, onCorrections, onRerun }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, busy]);

  // New analysis -> new conversation
  useEffect(() => { setMessages([]); setError(''); }, [analysisId]);

  const ask = async (question) => {
    const q = (question ?? input).trim();
    if (!q || busy) return;
    setError('');
    setInput('');
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { role: 'user', content: q }]);
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/dp-analysis/${analysisId}/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: q, history }),
      });
      if (!res.ok) throw new Error('chat failed');
      const data = await res.json();
      setMessages((m) => [...m, {
        role: 'assistant',
        content: data.answer,
        citations: data.citations || [],
      }]);
      if (data.correctionsChanged && onCorrections) onCorrections(data.corrections || '');
      if (data.rerunStarted && onRerun) onRerun();
    } catch (err) {
      console.error(err);
      setError('Något gick fel. Försök igen.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dpc">
      <div className="dpc-log">
        {messages.length === 0 && !busy && (
          <div className="dpc-empty">
            <p>
              Ställ frågor om dokumentet eller om analysen. Svaren är grundade i
              planhandlingen och citerar sidor — klicka på en sidhänvisning för
              att se stället i dokumentet.
            </p>
            <p>
              Du kan också lämna rättelser och uppdateringar, till exempel
              ”det finns en F–6-skola på Storgatan 12”. De sparas, och när du ber
              chatten köra om analysen kommer de med i analysen, rapporten och
              beslutsbilderna.
            </p>
            <div className="dpc-suggestions">
              {SUGGESTIONS.map((s) => (
                <button key={s} type="button" className="dpc-suggestion" onClick={() => ask(s)} data-tip="dp.suggestion">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`dpc-msg dpc-${m.role}`}>
            {m.role === 'assistant' ? (
              <>
                <div className="dpc-md">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeCitations]}
                    components={{
                      cite: ({ children }) => {
                        const txt = Array.isArray(children) ? children.join('') : String(children ?? '');
                        const page = parseInt((txt.match(/\d+/) || [])[0], 10);
                        if (!page || !onOpenPage) return <span className="dpc-cite-flat">{children}</span>;
                        return (
                          <button type="button" className="dpc-cite"
                                  data-tip={`Visa sida ${page} i dokumentet`}
                                  onClick={() => onOpenPage(page)}>
                            {children}
                          </button>
                        );
                      },
                    }}
                  >
                    {m.content}
                  </ReactMarkdown>
                </div>
                {m.citations && m.citations.length > 0 && (
                  <div className="dpc-sources">
                    Källor:{' '}
                    {m.citations.map((c) => (
                      <button key={c.page} type="button" className="dpc-source-btn"
                              data-tip={`Visa sida ${c.page} i dokumentet`}
                              onClick={() => onOpenPage?.(c.page)}>
                        s. {c.page}
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p>{m.content}</p>
            )}
          </div>
        ))}

        {busy && <div className="dpc-msg dpc-assistant"><p className="dpc-thinking">Läser dokumentet…</p></div>}
        {error && <p className="dpc-error">{error}</p>}
        <div ref={endRef} />
      </div>

      <form className="dpc-input" onSubmit={(e) => { e.preventDefault(); ask(); }}>
        <input
          type="text"
          value={input}
          placeholder="Fråga om dokumentet, eller lämna en rättelse…"
          onChange={(e) => setInput(e.target.value)}
          disabled={busy}
        />
        <button type="submit" disabled={busy || !input.trim()} data-tip="dp.ask">Fråga</button>
      </form>
    </div>
  );
};

export default DPChat;
